import { appPath, baseUrl } from '@fs/zion-config'
import { flagsAreReady, getFeatureFlag } from '@fs/zion-flags'
import { getCachedValuePromise } from '../hooks/useCache'
import { getGivenNames, getSurnames, isValidPid, LCR_URL, getWorkingPerson, isPlaceholderName } from './helpers'
import { simpleNameReasons } from './constants'

export async function determineWhetherToGoToMatchPage(persons, isTryDemo) {
  if (isValidPid(persons?.[0]?.id, isTryDemo)) return false
  // Check if the navigational logic should conditionally send the user to the match step
  // NOTE: If you change the flag value, you will need to refresh the page before this function returns the new value.
  await flagsAreReady()
  return getFeatureFlag('FTTE_FTTE_match').isOn
}

export async function getNextTo(
  currentStep,
  state = {},
  metadata = {},
  isTryDemo = false,
  ordinanceStatusReasons = []
) {
  const { coupleRelationships, memberUuid, membershipInfo, persons } = state
  const { connectingParent, gapParents, relation } = metadata
  let nextTo = '/add-name'

  switch (currentStep) {
    case 'relation': {
      nextTo = '/add-name/tree-list'
      break
    }
    case 'tree-list': {
      // REMEMBER: persons[0] is only the working person AFTER the tree-list step.
      //           That's why we have to manually `.find` the TARGET person here.
      const newWorkingPerson = getWorkingPerson(persons) ?? {}
      const givenNames = getGivenNames(newWorkingPerson)
      if (!isValidPid(newWorkingPerson.id, isTryDemo) || isPlaceholderName(givenNames, newWorkingPerson.sex))
        nextTo = '/add-name/name'
      else {
        const wasLivingPerson = newWorkingPerson?.living

        // Get name data
        const firstNames = givenNames
        const lastNames = getSurnames(newWorkingPerson)
        const hasName = !!(firstNames || lastNames)

        // Get vital data
        const isSpouse = relation === 'spouse'
        const marriageEventWithSpouse = coupleRelationships.find((relationship) =>
          [relationship.person1.id, relationship.person2.id].includes(newWorkingPerson?.id)
        )?.marriage
        const hasStandardizedVitalData =
          (newWorkingPerson?.birthDate?.standard ||
            newWorkingPerson?.deathDate?.standard ||
            (isSpouse && marriageEventWithSpouse?.date?.isStandard)) &&
          (newWorkingPerson?.birthPlace?.standard ||
            newWorkingPerson?.deathPlace?.standard ||
            (isSpouse && marriageEventWithSpouse?.place?.isStandard))
        const isTooRecentlyDeceased = newWorkingPerson?.baptism?.statusReasons?.includes('TOO_RECENTLY_DECEASED')
        const isDeathBeforeBirth = newWorkingPerson?.baptism?.statusReasons?.includes('DEATH_BEFORE_BIRTH')

        if (wasLivingPerson) {
          nextTo = '/add-name/vitals'
        } else if (hasName && ordinanceStatusReasons.some((reason) => simpleNameReasons.includes(reason))) {
          nextTo = '/add-name/name'
        } else if (hasName && hasStandardizedVitalData && !isTooRecentlyDeceased && !isDeathBeforeBirth) {
          nextTo = '/add-name/confirm'
        } else if (hasName && (!hasStandardizedVitalData || isTooRecentlyDeceased || isDeathBeforeBirth)) {
          nextTo = '/add-name/vitals'
        } else {
          nextTo = '/add-name/name'
        }
      }

      break
    }
    case 'name': {
      nextTo = '/add-name/vitals'
      break
    }
    case 'vitals': {
      nextTo = '/add-name/confirm'
      break
    }
    case 'confirm': {
      const shouldCheckForMatches = await determineWhetherToGoToMatchPage(persons, isTryDemo)
      nextTo = `/add-name/${shouldCheckForMatches ? 'match' : 'reserve'}`

      if (relation === 'child') {
        const hasGapParent = connectingParent?.idType === 'TEMPORARY'
        const isMemberMale = membershipInfo.sex.toUpperCase() === 'MALE'
        const selectableParents = isMemberMale ? gapParents.mothers : gapParents.fathers
        if (hasGapParent && selectableParents?.length > 0) nextTo = '/add-name/select-parents'
      } else if (relation === 'sibling') {
        const hasGapParent = connectingParent?.idType === 'TEMPORARY'
        if (hasGapParent) nextTo = '/add-name/select-parents'
      } else if (relation === 'grandparent') {
        // Connecting parent exists if person was selected from tree list or user is adding a grandparent to the
        // father's or mother's side for the first time (often the case with an empty tree)
        const hasConnectingParent = connectingParent?.idType
        const grandparentHasGapParents = !![...(gapParents.mothers ?? []), ...(gapParents.fathers ?? [])].length
        // We only want to go to the select-parents step if user is adding a grandparent not listed
        // This means the connecting parent object is empty and there are gap parents to choose from
        if (!hasConnectingParent && grandparentHasGapParents) nextTo = '/add-name/select-parents'
      }

      break
    }
    case 'edit': {
      nextTo = '/add-name/confirm'
      break
    }
    case 'select-parents': {
      const shouldCheckForMatches = await determineWhetherToGoToMatchPage(persons)
      nextTo = `/add-name/${shouldCheckForMatches ? 'match' : 'reserve'}`
      if (relation === 'sibling') {
        const { fatherId, motherId } = gapParents
        if (fatherId === 'ADD FATHER') nextTo = '/add-name/father'
        else if (motherId === 'ADD MOTHER') nextTo = '/add-name/mother'
      }
      break
    }
    case 'father':
    case 'mother': {
      const shouldCheckForMatches = await determineWhetherToGoToMatchPage(persons)
      nextTo = `/add-name/${shouldCheckForMatches ? 'match' : 'reserve'}`
      const { motherId } = gapParents
      if (currentStep === 'father' && motherId === 'ADD MOTHER') nextTo = '/add-name/mother'
      break
    }
    case 'name-correction': {
      const shouldCheckForMatches = await determineWhetherToGoToMatchPage(persons)
      nextTo = `/add-name/${shouldCheckForMatches ? 'match' : 'reserve'}`
      break
    }
    case 'vitals-correction': {
      const shouldCheckForMatches = await determineWhetherToGoToMatchPage(persons)
      nextTo = `/add-name/${shouldCheckForMatches ? 'match' : 'reserve'}`
      break
    }
    case 'death-before-eight': {
      nextTo = '/add-name'
      const workingPerson = persons?.find((person) => person.matchPurpose === 'TARGET')
      if (workingPerson) nextTo = `/add-name/vitals-correction`
      break
    }
    case 'stillborn': {
      nextTo = '/add-name'
      break
    }
    case 'not-available': {
      nextTo = '/add-name'
      break
    }
    case 'ordinances-available-completed-reserved': {
      nextTo = '/add-name'
      const workingPerson = persons?.find((person) => person.matchPurpose === 'TARGET')
      if (!workingPerson) break
      const baptismIsReserved = workingPerson?.baptism.status === 'RESERVED'
      const confirmationIsAvailable =
        workingPerson?.confirmation.status !== 'COMPLETED' && workingPerson?.confirmation.status !== 'RESERVED'
      if (!baptismIsReserved && confirmationIsAvailable) nextTo = '/add-name/reserve'
      break
    }
    case 'match': {
      nextTo = '/add-name/reserve'
      break
    }
    case 'ordinances-ready-reserve':
    case 'reserve': {
      nextTo = '/add-name/names-ready'
      break
    }
    case 'names-ready': {
      nextTo = '/add-name/print'
      break
    }
    case 'print': {
      nextTo = '/add-name/finish'
      break
    }
    case 'finish': {
      const isBishopricExperience = !!memberUuid
      nextTo = isBishopricExperience ? LCR_URL : '/'
      break
    }
    default: {
      nextTo = '/add-name'
    }
  }

  return nextTo
}

export async function getBackTo(currentStep, state = {}, metadata = {}, isTryDemo = false) {
  let backTo = '/add-name'
  const { memberUuid, persons } = state
  const { gapParents, wasLivingPerson } = metadata

  switch (currentStep) {
    case 'relation': {
      backTo = '/add-name'
      break
    }
    case 'tree-list': {
      backTo = '/add-name/relation'
      break
    }
    case 'name': {
      backTo = '/add-name/tree-list'
      break
    }
    case 'vitals': {
      backTo = `/add-name/${wasLivingPerson ? 'tree-list' : 'name'}`
      break
    }
    case 'confirm': {
      const workingPerson = persons[0]
      backTo = `/add-name/${isValidPid(workingPerson?.id, isTryDemo) && !wasLivingPerson ? 'tree-list' : 'vitals'}`
      break
    }
    case 'edit': {
      backTo = '/add-name/confirm'
      break
    }
    case 'select-parents': {
      backTo = '/add-name/confirm'
      break
    }
    case 'father': {
      backTo = '/add-name/select-parents'
      const { fathers } = gapParents
      if (!fathers?.length) backTo = '/add-name/confirm'
      break
    }
    case 'mother': {
      backTo = '/add-name/select-parents'
      const { fatherId, mothers } = gapParents
      if (fatherId === 'ADD FATHER') backTo = '/add-name/father'
      else if (!mothers?.length) backTo = '/add-name/confirm'
      break
    }
    case 'match': {
      if (metadata?.lastCompletedStep) {
        backTo = `/add-name/${metadata?.lastCompletedStep}`
      } else {
        backTo = '/add-name/confirm'
      }
      break
    }
    case 'reserve': {
      backTo = '/add-name/edit'
      break
    }
    case 'print': {
      backTo = '/add-name/names-ready'
      break
    }
    case 'finish': {
      const skippedPrint = await getCachedValuePromise(`skippedPrint:${memberUuid}`)
      backTo = skippedPrint ? '/add-name/names-ready' : '/add-name/print'
      break
    }
    default: {
      backTo = '/add-name'
    }
  }

  return backTo
}

// This makes sure the user has a member account before they can access the FNA app.
export function getMemberAccountNavigateFnaUrl(signedIn, verifyAccountBackUrl) {
  const homePageReturn = encodeURIComponent(appPath)
  // NOTE: By sending the user to this URL, they will be asked to enter their MRN before they can continue to out app.
  //       This URL also checks to see if their account is already a member account; in which case, they're automatically redirected to the URL in the `returnUrl` query param.
  const getStartedMemberUrl = `${baseUrl}/identity/settings/temple-experience`

  return {
    linkDropSearch: true,
    linkExternal: signedIn,
    linkSearch: `?returnUrl=${signedIn ? homePageReturn : encodeURIComponent(verifyAccountBackUrl)}`,
    linkUrl: signedIn ? getStartedMemberUrl : '/verify-account',
  }
}
